import React, { useState, useCallback, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Lightbox from 'react-image-lightbox';
import { BsPaperclip } from 'react-icons/bs';
import { useDropzone } from 'react-dropzone';
import { MdCloudUpload } from 'react-icons/md';
import Box from '@material-ui/core/Box';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import i18n from 'i18n-js';
import { useConfirm } from 'hooks';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import LinearProgress from '@material-ui/core/LinearProgress';

const imagePattern =
  /(?:(?:https?:\/\/))[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b(?:[-a-zA-Z0-9@:%_+.~#?&/=]*(\.jpg|\.png|\.jpeg))/g;

const ligthBoxStyles = {
  overlay: {
    zIndex: '1300',
  },
};

const useStyles = makeStyles((theme) => ({
  fileInput: {
    boxSizing: 'border-box',
    borderRadius: '4px',
    backgroundColor: '#F2F2F2',
    borderWidth: 2,
    borderColor: '#A8A8A8',
    borderStyle: 'dashed',
    padding: '30px 20px',
    transition: 'border .24s ease-in-out',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    '&:focus': {
      outline: 'none',
    },
  },
  active: {
    borderColor: theme.palette.primary.main,
  },
  accepted: {
    borderColor: theme.palette.primary.success,
  },
  rejected: {
    borderColor: theme.palette.error.main,
  },
  fileInputCaption: {
    userSelect: 'none',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  fileInputCaptionAction: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  fileList: {
    margin: 0,
    padding: 0,
    marginTop: theme.spacing(1.5),
    maxWidth: '100%',
  },
  fileItem: {
    display: 'flex',
  },
  fileName: {
    color: theme.palette.text.subtitle,
    marginLeft: '6px',
    maxWidth: '100%',
    paddingRight: 30,
  },
  inlineAction: {
    fontWeight: 500,
    textTransform: 'none',
  },
  progress: {
    marginBottom: 5,
    marginTop: 5,
  },
}));

const FileItem = ({
  file,
  removeFile,
  onReplaceFile,
  isUploading,
  fetchedUploadFile,
  setFile,
  hideDelete,
}) => {
  const classes = useStyles();
  const [showPreview, setShowPreview] = useState(false);
  const url = file.url_path || URL.createObjectURL(file);

  const name = file.name || file.filename;
  const linkRef = useRef(null);

  const [toggleDeleteFile, DeleteFileConfirm] = useConfirm({
    title: i18n.t('documents.delete_file'),
    message: i18n.t('documents.delete_file_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: removeFile,
  });

  const downloadFile = useCallback(() => {
    if (linkRef) {
      linkRef.current.click();
    }
  }, [linkRef]);

  useEffect(() => {
    if (!fetchedUploadFile && !isUploading && file) {
      setFile();
    }
  }, [fetchedUploadFile, isUploading]) // eslint-disable-line

  return (
    <>
      <ul className={classes.fileList}>
        {showPreview && (
          <Lightbox
            mainSrc={url}
            onCloseRequest={() => setShowPreview(false)}
            reactModalStyle={ligthBoxStyles}
          />
        )}
        <li className={classes.fileItem} key={file.path || url}>
          <Box minWidth={20}>
            <BsPaperclip size={20} color="#4D4F5C" />
          </Box>
          <Box maxWidth="100%">
            <Typography className={classes.fileName} variant="body2" noWrap component="span">
              {name}
              {isUploading && (
                <LinearProgress
                  classes={{
                    root: classes.progress,
                  }}
                />
              )}
            </Typography>
            <ButtonGroup variant="text" color="primary" aria-label="file action" size="small">
              {url.match(imagePattern) ? (
                <Button className={classes.inlineAction} onClick={() => setShowPreview(true)}>
                  {i18n.t('documents.view_file')}
                </Button>
              ) : null}
              <Button color="primary" className={classes.inlineAction} onClick={downloadFile}>
                <a href={url} download={name} ref={linkRef} hidden>
                  {i18n.t('documents.download_file')}
                </a>
                {i18n.t('documents.download_file')}
              </Button>
              <Button className={classes.inlineAction} onClick={onReplaceFile}>
                {i18n.t('documents.replace_file')}
              </Button>
              {!hideDelete && (
                <Button className={classes.inlineAction} onClick={toggleDeleteFile}>
                  {i18n.t('documents.delete_file')}
                </Button>
              )}
            </ButtonGroup>
          </Box>
        </li>
      </ul>
      <DeleteFileConfirm />
    </>
  );
};

function VersionFileInput(props) {
  const classes = useStyles();
  const [file, setFile] = useState(props.file);

  useEffect(() => {
    if (props.file) {
      setFile(props.file);
    }
  }, [props.file]) // eslint-disable-line

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles.find((f) => f);
      const image = new Image();
      image.addEventListener('load', () => {
        if (props.size && (image.width > props.size?.width || image.height > props.size?.height)) {
          return props.onError();
        }
        if (props.size) {
          props.onChange(props.name, acceptedFiles[0]);
          setFile(acceptedFiles[0]);
        }
      });
      image.src = URL.createObjectURL(file);

      if (!props.size) {
        props.onChange(props.name, acceptedFiles[0]);
        setFile(acceptedFiles[0]);
      }
    },
    [props]
  );

  const removeFile = useCallback(() => {
    if (props.onRemoveFile) {
      props.onRemoveFile();
    }
    setFile();
  }, [props]);

  const {
    open: openFileManager,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    noClick: true,
  });

  const updateFile = () => {
    setFile(props.file);
  };

  return (
    <>
      <input {...getInputProps()} />
      {file ? (
        <FileItem
          file={file}
          removeFile={removeFile}
          onReplaceFile={openFileManager}
          isUploading={props.isUploading}
          setFile={updateFile}
          hideDelete={props.hideDelete}
          fetchedUploadFile={props.fetchedUploadFile}
        />
      ) : (
        <Box
          className={clsx(
            classes.fileInput,
            isDragReject && classes.rejected,
            isDragAccept && classes.accepted,
            isDragActive && classes.active
          )}
          mt={2}
          width="100%"
          {...getRootProps()}
        >
          <MdCloudUpload size={34} color="rgba(0, 0, 0, 0.54)" />
          <Typography className={classes.fileInputCaption} variant="body2">
            {i18n.t('documents.drop_file_instructions1')}
            <span onClick={openFileManager} className={classes.fileInputCaptionAction}>
              {i18n.t('documents.drop_file_instructions2')}
            </span>
          </Typography>
        </Box>
      )}
    </>
  );
}

export default VersionFileInput;
